/**
 * NavigatorVoiceConversation — active voice conversation modal.
 *
 * Uses ElevenLabs @elevenlabs/react SDK with prompt override.
 * Shows live transcript, timer, end button.
 *
 * SPEC: MODULES/M7_NAVIGATOR/SPEC_M7_NAVIGATOR.md
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useConversation, ConversationProvider } from '@elevenlabs/react'
import { acquireVoiceLock, releaseVoiceLock } from '@/lib/voiceSessionLock'

interface TranscriptEntry {
  speaker: 'navigator' | 'participant'
  text: string
  timestamp: number
}

function NavigatorVoiceConversationInner({
  voiceAgentId,
  prompt,
  firstMessage,
  conversationType,
  onEnd,
  onClose,
}: {
  voiceAgentId: string
  prompt: string
  firstMessage: string
  conversationType: string
  onEnd: (transcript: string, transcriptJson: TranscriptEntry[], durationSeconds: number) => void
  onClose: () => void
}) {
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([])
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [started, setStarted] = useState(false)
  const startTimeRef = useRef(Date.now())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const transcriptEndRef = useRef<HTMLDivElement>(null)

  const conversation = useConversation({
    onMessage: useCallback((msg: { message: string; source: string }) => {
      const speaker = msg.source === 'ai' ? 'navigator' : 'participant'
      // Strip ElevenLabs voice annotations
      const text = msg.message.replace(/\[.*?\]/g, '').trim()
      if (!text) return
      setTranscript(prev => [...prev, {
        speaker,
        text,
        timestamp: Math.floor((Date.now() - startTimeRef.current) / 1000),
      }])
    }, []),
    onError: useCallback((err: Error) => {
      setError(err.message)
    }, []),
  })

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [transcript])

  // Start session on mount (with global voice lock)
  const lockId = useRef(`nav_${Date.now()}`).current
  useEffect(() => {
    if (!acquireVoiceLock(lockId)) {
      setError('Another voice session is already active')
      return
    }
    const start = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
        await conversation.startSession({
          agentId: voiceAgentId,
          clientTools: {},
          overrides: {
            agent: {
              prompt: { prompt },
              ...(firstMessage ? { firstMessage } : {}),
            },
          },
        })
        startTimeRef.current = Date.now()
        setStarted(true)
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000))
        }, 1000)
      } catch (err) {
        releaseVoiceLock(lockId)
        setError(err instanceof Error ? err.message : 'Failed to start voice session')
      }
    }
    start()

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      releaseVoiceLock(lockId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEnd = async () => {
    if (timerRef.current) clearInterval(timerRef.current)
    try {
      await conversation.endSession()
    } catch { /* may already be closed */ }

    // Build plain text transcript
    const plainText = transcript.map(t =>
      `${t.speaker === 'navigator' ? 'Navigator' : 'Participant'}: ${t.text}`
    ).join('\n\n')

    onEnd(plainText, transcript, elapsed)
  }

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  const typeLabel = conversationType === 'intro' ? 'Preparation' :
    conversationType === 'outro' ? 'Reflection' : 'Advisory'

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-0 md:p-4">
      <div className="bg-card border-0 md:border md:border-border md:rounded-xl shadow-2xl w-full h-full md:max-w-2xl md:max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
            <div>
              <h2 className="font-heading text-body font-semibold text-text-primary">Navigator</h2>
              <span className="font-data text-[10px] text-text-secondary uppercase tracking-wider">{typeLabel} Session</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-data text-caption text-text-secondary">{formatTime(elapsed)}</span>
            {started && (
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" title="Connected" />
            )}
          </div>
        </div>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {error && (
            <div className="bg-danger/10 border border-danger/30 rounded p-3 text-danger font-body text-body-sm">
              {error}
            </div>
          )}
          {transcript.length === 0 && !error && (
            <div className="text-text-secondary/40 font-body text-body-sm italic text-center py-8">
              {started ? 'Listening...' : 'Connecting to Navigator...'}
            </div>
          )}
          {transcript.map((entry, i) => (
            <div key={i} className={`flex ${entry.speaker === 'navigator' ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                entry.speaker === 'navigator'
                  ? 'bg-base text-text-primary'
                  : 'bg-action/10 text-text-primary'
              }`}>
                <div className="font-body text-[10px] text-text-secondary/50 mb-0.5">
                  {entry.speaker === 'navigator' ? 'Navigator' : 'You'} · {formatTime(entry.timestamp)}
                </div>
                <div className="font-body text-body-sm leading-relaxed">
                  {entry.text}
                </div>
              </div>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="font-body text-caption text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleEnd}
            className="font-body text-caption font-medium bg-danger/10 text-danger px-4 py-1.5 rounded hover:bg-danger/20 transition-colors"
          >
            End Conversation
          </button>
        </div>
      </div>
    </div>
  )
}

export function NavigatorVoiceConversation(props: Parameters<typeof NavigatorVoiceConversationInner>[0]) {
  return (
    <ConversationProvider>
      <NavigatorVoiceConversationInner {...props} />
    </ConversationProvider>
  )
}
